import React, { useState } from 'react';
import styled from 'styled-components';

const ExperienceContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--secondary-color);
    margin: 0.5rem auto 0;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 900px;
  margin: 0 auto;
  background-color: var(--white);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  box-shadow: var(--shadow-sm);
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const TabButtons = styled.div`
  display: flex;
  overflow-x: auto;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: column;
    min-width: 200px;
    margin-bottom: 0;
    border-left: none;
    border-right: 1px solid var(--border-color);
    overflow-x: visible;
  }
`;

const TabButton = styled.button`
  padding: 1rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? 'var(--secondary-color)' : 'transparent'};
  color: ${props => props.$active ? 'var(--secondary-color)' : 'var(--text-color)'};
  font-weight: ${props => props.$active ? '600' : '400'};
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: var(--transition);
  
  @media (min-width: 768px) {
    border-bottom: none;
    border-left: 2px solid ${props => props.$active ? 'var(--secondary-color)' : 'transparent'};
    padding: 1rem 1.5rem;
  }
  
  &:hover {
    background-color: var(--light-gray);
    color: var(--secondary-color);
  }
`;

const TabContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const CompanyName = styled.h4`
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 0.4rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-size: 0.8rem;
`;

const CompanyIntro = styled.p`
  font-size: 0.95rem;
  color: var(--dark-gray);
  line-height: 1.6;
  margin-bottom: 1.75rem;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 6px;
    bottom: 6px;
    width: 1px;
    background-color: var(--border-color);
  }
`;

const RoleBlock = styled.div`
  position: relative;
  padding-bottom: 2.25rem;

  &:last-child {
    padding-bottom: 0;
  }
`;

const RoleDot = styled.div`
  position: absolute;
  left: -1.5rem;
  top: 6px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background-color: ${props => props.$current ? 'var(--secondary-color)' : 'var(--white)'};
  border: 2px solid var(--secondary-color);
`;

const RoleHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 1rem;
`;

const JobTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const PromotionBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #16a34a;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
`;

const JobPeriod = styled.p`
  font-size: 0.9rem;
  color: var(--dark-gray);
  font-style: italic;
`;

const JobResponsibilities = styled.ul`
  list-style: none;
`;

const Responsibility = styled.li`
  position: relative;
  padding-left: 1.75rem;
  margin-bottom: 1rem;
  line-height: 1.6;
  font-size: 0.975rem;
  
  &::before {
    content: '▹';
    position: absolute;
    left: 0;
    color: var(--secondary-color);
    font-size: 1.1rem;
  }
`;

const Experience = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  const jobs = [
    {
      company: 'Marmin Ai',
      intro: 'A Dubai-based AI-driven fintech company specialising in e-invoicing compliance and ERP integration solutions for enterprise clients across the UAE.',
      roles: [
        {
          title: 'Consultant Software Developer',
          period: 'Jan 2026 – Present',
          responsibilities: [
            'Designed and developed secure integration services connecting ERP systems (Sage X3, SAP, Oracle) with e-invoicing gateways, enabling seamless electronic invoice exchange and regulatory compliance.',
            'Implemented robust authentication and authorisation mechanisms using OAuth 2.0 and JWT, securing access to sensitive financial data across integration endpoints.',
            'Performed advanced data transformation and mapping across XML, JSON, and CSV formats to support accurate invoice processing and interoperability between disparate systems.',
            'Delivered Java & Python SDKs adopted by 20+ enterprise clients, reducing integration time from weeks to days and improving adoption rate.',
            'Enabled successful FTA e-invoicing compliance for multiple organisations in the UAE market, supporting thousands of compliant e-invoices daily with 99.9%+ uptime.',
          ]
        }
      ]
    },
    {
      company: 'Newgen Software',
      intro: 'A global leader in business process management and content management software, serving banks, insurance companies, and government agencies across 60+ countries.',
      roles: [
        {
          title: 'Senior Software Engineer',
          period: 'July 2024 – Oct 2025',
          promoted: true,
          responsibilities: [
            'Designed and developed Java & Spring Boot solutions for multiple Indian banks covering customer onboarding, KYC verification, and loan origination.',
            'Productised a multi-tenant middleware platform with pluggable connectors and a unified banking API, cutting go-live time from weeks to days and enabling 8+ teams to integrate without re-implementation — reducing defects by 30–50%.',
            'Led the end-to-end integration of the Perfios AI/ML Bank Statement Analyzer at Bank of Baroda, replacing manual verification with a real-time automated workflow and reducing credit processing time from hours to minutes.',
            'Developed complex stored procedures and optimised SQL queries, reducing report generation time by 60%.',
            'Mentored 3 junior developers, conducting code reviews and guiding them on clean code principles and Java best practices.',
          ]
        },
        {
          title: 'Software Engineer',
          period: 'Aug 2022 – July 2024',
          responsibilities: [
            'Contributed to Newgen\'s suite of products — IBPS, Omnidocs, and Omniscan.',
            'Collaborated closely with Bank of Baroda\'s business team to gather, analyse, and document requirements for their Loan Origination System (LOS).',
            'Worked on multiple third-party integrations including CIBIL, NSDL, Experian, and Vidhyalaxmi.',
            'Deployed software updates and enhancements following best practices, minimising downtime and resolving critical production issues.',
            'Collaborated with cross-functional teams (QA, BA, DevOps) in Agile ceremonies to deliver biweekly releases consistently on schedule.',
          ]
        }
      ]
    },
    {
      company: 'Kasmasoft',
      intro: 'A boutique software startup based in London focused on building mobile applications, including faith-based productivity tools for the Muslim community.',
      roles: [
        {
          title: 'Project Intern',
          period: 'July 2021 – April 2022',
          responsibilities: [
            'Designed and implemented a full-stack mobile application supporting daily Islamic prayer routines.',
            'Integrated location-aware prayer time notifications and Qibla compass using GPS APIs.',
            'Developed a personalised prayer tracker and "Fast Tracking" module with streak metrics.',
            'Enabled Google Sign-In for seamless authentication.',
            'Built a Google Maps-based mosque and halal food discovery feature.',
          ]
        }
      ]
    },
  ];
  
  return (
    <ExperienceContainer>
      <SectionTitle>Work Experience</SectionTitle>
      
      <TabsContainer>
        <TabButtons>
          {jobs.map((job, index) => (
            <TabButton 
              key={index} 
              $active={activeTab === index}
              onClick={() => setActiveTab(index)}
            >
              {job.company}
            </TabButton>
          ))}
        </TabButtons>
        
        <TabContent>
          <CompanyName>{jobs[activeTab].company}</CompanyName>
          <CompanyIntro>{jobs[activeTab].intro}</CompanyIntro>

          <Timeline>
            {jobs[activeTab].roles.map((role, i) => (
              <RoleBlock key={i}>
                <RoleDot $current={i === 0} />
                <RoleHeader>
                  <JobTitle>
                    {role.title}
                    {role.promoted && <PromotionBadge>Promoted</PromotionBadge>}
                  </JobTitle>
                  <JobPeriod>{role.period}</JobPeriod>
                </RoleHeader>
                <JobResponsibilities>
                  {role.responsibilities.map((resp, j) => (
                    <Responsibility key={j}>{resp}</Responsibility>
                  ))}
                </JobResponsibilities>
              </RoleBlock>
            ))}
          </Timeline>
        </TabContent>
      </TabsContainer>
    </ExperienceContainer>
  );
};

export default Experience;
